import type {
  DomainCanvas,
  DomainDocument,
  EntityId,
  JsonValue,
  KnowledgeItem,
  SaveReceipt,
} from "@note-app/contracts";
import { CURRENT_CONTENT_SCHEMA_VERSION } from "@note-app/contracts";
import {
  ValidationError,
  createKnowledgeItem,
  migrateCanvasToCurrent,
  migrateDocumentToCurrent,
  type Clock,
  type IdGenerator,
} from "@note-app/domain";
import {
  EntityNotFoundError,
  type UnitOfWork,
} from "@note-app/storage";

export interface ApplicationDependencies {
  readonly unitOfWork: UnitOfWork;
  readonly clock: Clock;
  readonly ids: IdGenerator;
}

export interface NewItemMetadata {
  title: string;
  icon?: string;
  folderId?: EntityId;
  tags?: readonly EntityId[];
  properties?: Readonly<Record<string, JsonValue>>;
}

export interface CreatedItem {
  item: KnowledgeItem;
  receipt: SaveReceipt;
}

export class CreateDocumentService {
  public constructor(private readonly dependencies: ApplicationDependencies) {}

  public execute(
    input: NewItemMetadata & { document?: DomainDocument },
  ): Promise<CreatedItem> {
    const now = this.dependencies.clock.now();
    const item = createKnowledgeItem({
      id: this.dependencies.ids.next(),
      kind: "document",
      title: input.title,
      createdAt: now,
      ...(input.icon === undefined ? {} : { icon: input.icon }),
      ...(input.folderId === undefined ? {} : { folderId: input.folderId }),
      ...(input.tags === undefined ? {} : { tags: input.tags }),
      ...(input.properties === undefined
        ? {}
        : { properties: input.properties }),
    });
    const document = migrateDocumentToCurrent(input.document ?? {
      schemaVersion: CURRENT_CONTENT_SCHEMA_VERSION,
      blocks: [],
    }).value;

    return this.dependencies.unitOfWork.transaction(async (repositories) => {
      await assertReferencesExist(
        repositories,
        item.folderId,
        item.tags,
      );
      await repositories.items.create(item);
      const receipt = await repositories.documents.save(
        item.id,
        document,
        0,
        now,
      );
      const stored = await repositories.items.get(item.id);
      if (stored === null) {
        throw new EntityNotFoundError("item", item.id);
      }
      return { item: stored, receipt };
    });
  }
}

export class SaveDocumentService {
  public constructor(
    private readonly unitOfWork: UnitOfWork,
    private readonly clock: Clock,
  ) {}

  public execute(input: {
    itemId: EntityId;
    document: DomainDocument;
    expectedRevision: number;
  }): Promise<SaveReceipt> {
    const document = migrateDocumentToCurrent(input.document).value;
    assertExpectedRevision(input.expectedRevision);
    return this.unitOfWork.repositories.documents.save(
      input.itemId,
      document,
      input.expectedRevision,
      this.clock.now(),
    );
  }
}

export class CreateCanvasService {
  public constructor(private readonly dependencies: ApplicationDependencies) {}

  public execute(
    input: NewItemMetadata & { canvas?: DomainCanvas },
  ): Promise<CreatedItem> {
    const now = this.dependencies.clock.now();
    const item = createKnowledgeItem({
      id: this.dependencies.ids.next(),
      kind: "canvas",
      title: input.title,
      createdAt: now,
      ...(input.icon === undefined ? {} : { icon: input.icon }),
      ...(input.folderId === undefined ? {} : { folderId: input.folderId }),
      ...(input.tags === undefined ? {} : { tags: input.tags }),
      ...(input.properties === undefined
        ? {}
        : { properties: input.properties }),
    });
    const canvas = migrateCanvasToCurrent(input.canvas ?? {
      schemaVersion: CURRENT_CONTENT_SCHEMA_VERSION,
      mode: "freeform",
      nodes: [],
      edges: [],
      settings: {},
    }).value;

    return this.dependencies.unitOfWork.transaction(async (repositories) => {
      await assertReferencesExist(
        repositories,
        item.folderId,
        item.tags,
      );
      await repositories.items.create(item);
      const receipt = await repositories.canvases.save(
        item.id,
        canvas,
        0,
        now,
      );
      const stored = await repositories.items.get(item.id);
      if (stored === null) {
        throw new EntityNotFoundError("item", item.id);
      }
      return { item: stored, receipt };
    });
  }
}

export class SaveCanvasService {
  public constructor(
    private readonly unitOfWork: UnitOfWork,
    private readonly clock: Clock,
  ) {}

  public execute(input: {
    itemId: EntityId;
    canvas: DomainCanvas;
    expectedRevision: number;
    title?: string;
  }): Promise<SaveReceipt> {
    const canvas = migrateCanvasToCurrent(input.canvas).value;
    assertExpectedRevision(input.expectedRevision);
    if (input.title !== undefined) {
      return this.unitOfWork.transaction(async (repositories) => {
        const current = await repositories.items.get(input.itemId);
        if (!current) throw new EntityNotFoundError("canvas", input.itemId);
        const title = input.title!.trim() || current.title;
        const now = Math.max(this.clock.now(), current.updatedAt);
        const renamed = title === current.title ? undefined : await repositories.items.update(
          input.itemId, { title }, input.expectedRevision, now,
        );
        return repositories.canvases.save(
          input.itemId, canvas, renamed?.revision ?? input.expectedRevision, now,
        );
      });
    }
    return this.unitOfWork.repositories.canvases.save(
      input.itemId,
      canvas,
      input.expectedRevision,
      this.clock.now(),
    );
  }
}

export class MoveToTrashService {
  public constructor(
    private readonly unitOfWork: UnitOfWork,
    private readonly clock: Clock,
  ) {}

  public async execute(input: {
    itemId: EntityId;
    expectedRevision: number;
  }): Promise<KnowledgeItem> {
    assertExpectedRevision(input.expectedRevision);
    return this.unitOfWork.repositories.items.moveToTrash(
      input.itemId,
      input.expectedRevision,
      this.clock.now(),
    );
  }
}

export type RestoreStrategy = "root-if-parent-unavailable";

export interface RestoreResult {
  readonly item: KnowledgeItem;
  readonly rehomedToRoot: boolean;
}

export class RestoreItemService {
  public constructor(
    private readonly unitOfWork: UnitOfWork,
    private readonly clock: Clock,
  ) {}

  public execute(input: {
    itemId: EntityId;
    expectedRevision: number;
    strategy?: RestoreStrategy;
  }): Promise<RestoreResult> {
    assertExpectedRevision(input.expectedRevision);
    return this.unitOfWork.transaction(async (repositories) => {
      const item = await repositories.items.get(input.itemId, {
        includeDeleted: true,
      });
      if (item === null) {
        throw new EntityNotFoundError("item", input.itemId);
      }
      let fallbackFolderId: EntityId | null | undefined;
      let rehomedToRoot = false;
      if (item.folderId !== undefined) {
        const folder = await repositories.folders.get(item.folderId, true);
        if (folder === null || folder.deletedAt !== undefined) {
          fallbackFolderId = null;
          rehomedToRoot = true;
        }
      }
      const restored = await repositories.items.restore(
        item.id,
        input.expectedRevision,
        this.clock.now(),
        fallbackFolderId,
      );
      return { item: restored, rehomedToRoot };
    });
  }
}

async function assertReferencesExist(
  repositories: UnitOfWork["repositories"],
  folderId: EntityId | undefined,
  tagIds: readonly EntityId[],
): Promise<void> {
  if (folderId !== undefined) {
    const folder = await repositories.folders.get(folderId);
    if (folder === null) {
      throw new EntityNotFoundError("folder", folderId);
    }
  }
  for (const tagId of tagIds) {
    const tag = await repositories.tags.get(tagId);
    if (tag === null) {
      throw new EntityNotFoundError("tag", tagId);
    }
  }
}

function assertExpectedRevision(value: number): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new ValidationError([
      {
        path: "expectedRevision",
        message: "must be a non-negative safe integer",
      },
    ]);
  }
}
